<h2 mat-dialog-title>{{ dialogTitle }}</h2>

<mat-dialog-content>
  <!-- Template info fields -->
  <div fxLayout="row" fxLayoutGap="16px" class="m-b-20">
    <mat-form-field fxFlex="66" appearance="outline">
      <mat-label>Template Name</mat-label>
      <input matInput [(ngModel)]="name"
             placeholder="e.g. QLD Singles 0202A">
    </mat-form-field>
    <mat-form-field fxFlex="34" appearance="outline">
      <mat-label>Render Code</mat-label>
      <input matInput [(ngModel)]="renderCode"
             placeholder="e.g. Q3-0202A">
    </mat-form-field>
  </div>
  <mat-form-field class="w-100 m-b-20" appearance="outline">
    <mat-label>Description (optional)</mat-label>
    <input matInput [(ngModel)]="description"
           placeholder="Brief description of this template group">
  </mat-form-field>

  <!-- Affected Homes Section -->
  <div class="section-block m-b-20">
    <div fxLayout="row" fxLayoutAlign="start center"
         fxLayoutGap="8px" class="m-b-10">
      <span class="section-badge section-badge-green">
        <mat-icon>home</mat-icon>
      </span>
      <strong>Affected Homes</strong>
      <small class="text-muted">
        &mdash; homes that will receive these IFC files
      </small>
    </div>

    <!-- Added homes chips -->
    <div *ngIf="affectedHomes.length > 0"
         fxLayout="row wrap" fxLayoutGap="8px" class="m-b-10">
      <mat-chip-list>
        <mat-chip *ngFor="let home of affectedHomes; let i = index"
                  [removable]="true"
                  (removed)="removeAffectedHome(i)"
                  class="affected-home-chip"
                  [ngClass]="{
                    'home-existing': home.isExisting && !home.isNew,
                    'home-new': !home.isExisting || home.isNew
                  }">
          {{ home.homeName }}
          <span *ngIf="home.isExisting && !home.isNew"
                class="home-tag existing-tag">EXISTING</span>
          <span *ngIf="home.isNew"
                class="home-tag new-tag">NEW</span>
          <mat-icon matChipRemove>cancel</mat-icon>
        </mat-chip>
      </mat-chip-list>
    </div>

    <!-- Add existing home -->
    <div fxLayout="row" fxLayoutAlign="start end" fxLayoutGap="8px">
      <mat-form-field fxFlex appearance="outline">
        <mat-label>Select existing home</mat-label>
        <mat-select [(ngModel)]="newAffectedHomeId"
                    [disabled]="loadingHomes">
          <mat-option *ngFor="let home of allHomes"
                      [value]="home.id">
            {{ home.name }}
          </mat-option>
        </mat-select>
      </mat-form-field>
      <button mat-flat-button color="accent"
              [disabled]="!newAffectedHomeId"
              (click)="addAffectedHome()"
              class="m-b-20">
        <mat-icon>add</mat-icon> Add
      </button>
    </div>

    <!-- Create new home -->
    <div fxLayout="row" fxLayoutAlign="start end" fxLayoutGap="8px">
      <mat-form-field fxFlex appearance="outline">
        <mat-label>Or type a new home name</mat-label>
        <input matInput [(ngModel)]="newHomeName"
               placeholder="e.g. Bayside 27"
               (keyup.enter)="addNewHome()">
      </mat-form-field>
      <button mat-flat-button color="primary"
              [disabled]="!newHomeName.trim()"
              (click)="addNewHome()"
              class="m-b-20">
        <mat-icon>add</mat-icon> Create
      </button>
    </div>
  </div>

  <!-- Source Mappings Section -->
  <div class="section-block m-b-20">
    <div fxLayout="row" fxLayoutAlign="space-between center"
         class="m-b-10">
      <div fxLayout="row" fxLayoutAlign="start center"
           fxLayoutGap="8px">
        <span class="section-badge section-badge-indigo">S</span>
        <strong>Source Mappings</strong>
        <small class="text-muted">
          &mdash; where IFC files are sourced from
        </small>
      </div>
      <button mat-stroked-button color="primary"
              (click)="addSourceMapping()">
        <mat-icon>add</mat-icon> Add Source
      </button>
    </div>

    <!-- Source mapping entries -->
    <div *ngFor="let mapping of sourceMappings; let i = index"
         class="source-mapping-card m-b-10">
      <div fxLayout="row" fxLayoutAlign="space-between center"
           class="m-b-10">
        <div fxLayout="row" fxLayoutAlign="start center"
             fxLayoutGap="8px">
          <span class="source-index-badge">{{ i + 1 }}</span>
          <strong>Source Home</strong>
        </div>
        <button mat-icon-button (click)="removeSourceMapping(i)"
                matTooltip="Remove source">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <!-- Builder + Home selectors -->
      <div fxLayout="row" fxLayoutGap="10px" class="m-b-10">
        <mat-form-field fxFlex="40" appearance="outline">
          <mat-label>Builder</mat-label>
          <mat-select [(ngModel)]="mapping.sourceCompanyId"
                      (selectionChange)="onSourceCompanyChange(mapping)">
            <mat-option *ngFor="let company of siblingCompanies"
                        [value]="company.id">
              {{ company.name }}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field fxFlex="60" appearance="outline">
          <mat-label>Source home</mat-label>
          <mat-select [(ngModel)]="mapping.sourceHomeId"
                      [disabled]="!mapping.sourceCompanyId || mapping.loadingHomes"
                      (selectionChange)="onSourceHomeChange(mapping)">
            <mat-option *ngFor="let home of mapping.availableHomes"
                        [value]="home.id">
              {{ home.name }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <!-- Loading indicator for files -->
      <mat-progress-bar *ngIf="mapping.loadingFiles"
                        mode="indeterminate" class="m-b-10">
      </mat-progress-bar>

      <!-- IFC file selection -->
      <ng-container *ngIf="mapping.availableFiles.length > 0">
        <!-- Master files -->
        <div *ngIf="getMasterFiles(mapping).length > 0"
             class="m-b-10">
          <div class="file-section-label">Master Files</div>
          <div fxLayout="row wrap" fxLayoutGap="6px">
            <button *ngFor="let file of getMasterFiles(mapping)"
                    mat-stroked-button
                    class="file-toggle-btn"
                    [class.file-toggle-active-master]="isFileSelected(mapping, file.id)"
                    (click)="toggleFileSelection(mapping, file.id)">
              <mat-icon *ngIf="isFileSelected(mapping, file.id)"
                        class="file-check-icon">check</mat-icon>
              {{ file.name }}
            </button>
          </div>
        </div>

        <!-- Option / facade files -->
        <div *ngIf="getOptionFiles(mapping).length > 0">
          <div class="file-section-label">
            Facade / Option Files
          </div>
          <div fxLayout="row wrap" fxLayoutGap="6px">
            <button *ngFor="let file of getOptionFiles(mapping)"
                    mat-stroked-button
                    class="file-toggle-btn"
                    [class.file-toggle-active-option]="isFileSelected(mapping, file.id)"
                    (click)="toggleFileSelection(mapping, file.id)">
              <mat-icon *ngIf="isFileSelected(mapping, file.id)"
                        class="file-check-icon">check</mat-icon>
              {{ file.name }}
            </button>
          </div>
        </div>
      </ng-container>

      <!-- Empty file state -->
      <div *ngIf="mapping.sourceHomeId
                   && !mapping.loadingFiles
                   && mapping.availableFiles.length === 0"
           class="empty-files-msg">
        No IFC files available for this home.
      </div>
    </div>

    <!-- Empty sources state -->
    <div *ngIf="sourceMappings.length === 0"
         class="empty-sources-msg">
      No source homes added yet. Click "+ Add Source" to map IFC files
      from existing homes.
    </div>
  </div>

  <!-- Summary bar -->
  <div *ngIf="sourceMappings.length > 0"
       class="summary-bar m-b-20"
       fxLayout="row" fxLayoutGap="24px">
    <div>
      <span class="summary-label">Sources:</span>
      <strong>
        {{ sourceMappings.length }}
      </strong>
    </div>
    <div>
      <span class="summary-label">Master Files:</span>
      <strong [class.text-warn]="totalSelectedMasterFiles === 0">
        {{ totalSelectedMasterFiles }}
      </strong>
    </div>
    <div>
      <span class="summary-label">Option Files:</span>
      <strong>{{ totalSelectedOptionFiles }}</strong>
    </div>
    <div>
      <span class="summary-label">Affected Homes:</span>
      <strong>{{ affectedHomes.length }}</strong>
    </div>
  </div>
</mat-dialog-content>

<mat-dialog-actions fxLayoutAlign="end center" fxLayoutGap="10px">
  <button mat-stroked-button (click)="onCancel()">Cancel</button>
  <button mat-flat-button color="primary"
          [disabled]="!canSave || saving"
          [class.button-spinner]="saving"
          (click)="onSave()">
    {{ data.isEdit ? 'Save Changes' : 'Create Template' }}
  </button>
</mat-dialog-actions>
